import React, { useState, useRef, useEffect } from "react";
import { message, Spin, Drawer } from "antd";
import { useGetUserInfo } from "hooks/useGetUserInfo";
import { useGetChatHistory } from "features/chatHistory/components/hooks/useGetChatHistory";
import ItemDetailsPage from "./item-details-page";
import { Item } from "../../../types";
import { END_POINT } from "config/environment";
import { extractTextFromAttachment } from "utils/fileUtils";
import { useOCRValidation } from "utils/ocr/useOCRValidation";
import { useFileProcessing } from "../hooks/useFileProcessing";
import CustomEmpty from "components/CustomEmpty";
import { BatchTableHistory } from "./BatchTableHistory";

const UploadPageHistory: React.FC = () => {
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [showDetailsPage, setShowDetailsPage] = useState(false);
  const [selectedItem, setSelectedItem] = useState<Item | null>(null);
  const [isMobileView, setIsMobileView] = useState(false);
  const [mobileDrawerVisible, setMobileDrawerVisible] = useState(false);
  const [selectedMobileItem, setSelectedMobileItem] = useState<Item | null>(
    null,
  );

  const {
    data: chatHistoryPage,
    isLoading: isHistoryLoading,
    refetch,
  } = useGetChatHistory({ page: currentPage, perPage: pageSize });

  const historyItems = chatHistoryPage?.items ?? [];
  const total = chatHistoryPage?.total ?? 0;

  const { token } = useGetUserInfo();

  const {
    ocrErrorLogs,
    showOcrErrors,
    clearOcrErrorLogs,
    checkDocumentRequiresOCR,
    addOcrErrorLog,
  } = useOCRValidation();

  const {
    processingItems,
    setProcessingItems,
    processingStatus,
    setProcessingStatus,
    progress,
    setProgress,
    generateChatId,
    startSSEForProcessing,
  } = useFileProcessing(checkDocumentRequiresOCR, addOcrErrorLog);

  const [jobDescription, setJobDescription] = useState<any>(null);
  const [jobDescriptionText, setJobDescriptionText] = useState<string>("");
  const [jdFile, setJdFile] = useState<File | null>(null);
  const [analysisResults, setAnalysisResults] = useState<{
    [key: string | number]: any[];
  }>({});

  const cvInputRef = useRef<HTMLInputElement | null>(null);
  const jobDescriptionTextRef = useRef<string>("");

  const { data: recentChatData, isLoading: isRecentLoading } =
    useGetChatHistory({ page: 1, perPage: 5 });

  const recentItems = recentChatData?.items ?? [];

  const latestHistoryItems = recentItems
    .filter(
      (item: { id: any }) =>
        !processingItems.some(
          (processingItem: { id: any }) => processingItem.id === item.id,
        ),
    )
    .slice(0, 5);

  // Handle window resize for responsive design
  useEffect(() => {
    const handleResize = () => {
      const isMobile = window.innerWidth < 768;
      setIsMobileView(isMobile);

      // Adjust page size for mobile
      if (isMobile && pageSize === 10) {
        setPageSize(5);
        setCurrentPage(1);
      } else if (!isMobile && pageSize === 5) {
        setPageSize(10);
        setCurrentPage(1);
      }
    };

    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [pageSize]);

  useEffect(() => {
    const extractJDText = async () => {
      if (jdFile) {
        try {
          const text = await extractTextFromAttachment(jdFile);
          setJobDescriptionText(text);
          jobDescriptionTextRef.current = text;
          message.success("Job description processed successfully");
        } catch (error) {
          console.error("Error extracting JD text:", error);
          message.error("Failed to extract text from job description");
          setJobDescriptionText("");
          jobDescriptionTextRef.current = "";
        }
      } else {
        setJobDescriptionText("");
        jobDescriptionTextRef.current = "";
      }
    };

    extractJDText();
  }, [jdFile]);

  const handleOpenDetailsPage = (itemId: string | any) => {
    const item = allItems.find((i) => i.id?.toString() === itemId?.toString());

    if (isMobileView) {
      setSelectedMobileItem(item || null);
      setMobileDrawerVisible(true);
    } else {
      setSelectedItem(item || null);
      setShowDetailsPage(true);
    }
  };

  const handleCloseDetailsPage = () => {
    setShowDetailsPage(false);
    setSelectedItem(null);
  };

  const handleCloseMobileDrawer = () => {
    setMobileDrawerVisible(false);
    setSelectedMobileItem(null);
  };

  const handleAddClick = async (item: any) => {
    if (!item) return;

    if (item.job_description) {
      setJobDescription({
        uid: `jd-${item.id}`,
        name: `JD-${item.id}.txt`,
        size: item.job_description.length,
        type: "text/plain",
      });

      setJobDescriptionText(item.job_description);
      jobDescriptionTextRef.current = item.job_description;
    }

    setActiveItemId(item.id);
  };

  const [activeItemId, setActiveItemId] = useState<any | null>(null);
  const [activeJDId, setActiveJDId] = useState<number | null>(null);

  const handleDeleteSession = async (item: Item) => {
    try {
      const response = await fetch(
        `${END_POINT.BASE_URL}/v1/delete-session/${item.id}`,
        {
          method: "DELETE",
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );

      if (!response.ok) {
        throw new Error("Failed to delete batch");
      }

      message.success("Batch deleted successfully");

      setProcessingItems((prev) => prev.filter((p) => p.id !== item.id));

      await refetch();
    } catch (error) {
      console.error("Error deleting batch:", error);
      message.error("Failed to delete batch");
    }
  };

  const handlePageChange = (page: number, newPageSize?: number) => {
    setCurrentPage(page);
    if (newPageSize && newPageSize !== pageSize) {
      setPageSize(newPageSize);
      setCurrentPage(1);
    }
  };

  const allItems = [...processingItems, ...historyItems];

  if (
    isHistoryLoading &&
    currentPage === 1 &&
    processingItems.length === 0 &&
    !showDetailsPage &&
    !mobileDrawerVisible
  ) {
    return (
      <div className="flex items-center justify-center h-screen">
        <Spin size="large" tip="Loading document history..." />
      </div>
    );
  }

  // Desktop details page
  if (!isMobileView && showDetailsPage && selectedItem) {
    return (
      <ItemDetailsPage
        item={selectedItem}
        analysisResults={analysisResults}
        onBack={handleCloseDetailsPage}
      />
    );
  }

  return (
    <>
      <div className="flex h-screen w-full max-w-full bg-gray-50 overflow-x-hidden">
        <div className="flex-1 p-3 sm:p-4 md:p-6">
          {isMobileView && (
            <div className="mb-4 pb-3 border-b border-gray-200">
              <h1 className="text-lg font-semibold text-gray-900">
                Document Batches
              </h1>
              <p className="text-xs text-gray-500 mt-1">
                Manage and track your uploaded documents
              </p>
            </div>
          )}

          <BatchTableHistory
            items={allItems}
            processingStatus={processingStatus}
            progress={progress}
            processingItems={processingItems}
            onItemClick={handleOpenDetailsPage}
            onAddClick={handleAddClick}
            onDeleteSession={handleDeleteSession}
            total={total + processingItems.length}
            currentPage={currentPage}
            pageSize={pageSize}
            onPageChange={handlePageChange}
            loading={isHistoryLoading && currentPage > 1}
          />

          {allItems.length === 0 && !isHistoryLoading && (
            <div className="mt-8">
              <CustomEmpty message="No document batches uploaded yet." />
            </div>
          )}
        </div>
      </div>

      <Drawer
        title={selectedMobileItem?.title || "Batch Details"}
        placement="right"
        width="100%"
        open={mobileDrawerVisible}
        onClose={handleCloseMobileDrawer}
        closable={true}
        destroyOnClose
        bodyStyle={{ padding: 0 }}
        headerStyle={{
          padding: "12px 16px",
          borderBottom: "1px solid #f0f0f0",
        }}
      >
        {selectedMobileItem && (
          <ItemDetailsPage
            item={selectedMobileItem}
            analysisResults={analysisResults}
            onBack={handleCloseMobileDrawer}
          />
        )}
      </Drawer>
    </>
  );
};

export default UploadPageHistory;
